/* ══════════════════════════════════════════════════════════════════════
   PRIVILEGE ACCESS GOVERNANCE — types, display metadata, and the
   API-shape -> graph/table transformers.

   The API shape is nested (application > account > user|group|role >
   policy). The graph needs a FLAT node list with parentId pointers so
   the layout can derive each node's column from its depth. buildPagTree
   does that conversion, so when the real endpoint lands only the fetch
   changes — every consumer below keeps working.
   ══════════════════════════════════════════════════════════════════════ */

/* ── API shape (mirrors the mock JSON exactly) ──────────────────────── */

export type PagSeverity = "High" | "Medium" | "Low" | "Uncategorized";

export interface PagPolicy {
  id: number;
  policyName: string;
  policyArn: string;
  severity: PagSeverity;
  updatedOn: number;
  softDelete: boolean;
}

export interface PagAccessKey {
  id: number;
  accessKeyId: string;
  userId: number;
  region: string;
  service: string;
  lastUsed: number;
  updatedOn: number;
  softDelete: boolean;
}

export interface PagUser {
  userName: string;
  mfaDevices: unknown[];
  accessKeys: PagAccessKey[];
  userPolicies: PagPolicy[];
}

/* Members are `groupUsers`, NOT `users` — the account level uses `users`
   for its own list, and the two are different shapes of the same thing.
   Both fields stay optional: a group with no members omits them. */
export interface PagGroup {
  groupName: string;
  groupUsers?: PagUser[];
  groupPolicies?: PagPolicy[];
}

export interface PagRole {
  roleName: string;
  serviceType: string;
  rolePolicies: PagPolicy[];
  instances: unknown[];
}

export interface PagAccount {
  accountId: string;
  accountName: string;
  users: PagUser[];
  groups: PagGroup[];
  roles: PagRole[];
}

export interface PagApplication {
  appCode: string;
  appName: string;
  accountIdDetails: PagAccount[];
}

/* ── Graph node ─────────────────────────────────────────────────────── */

export type PagNodeKind =
  | "application"
  | "account"
  | "user"
  | "group"
  | "role"
  | "policy";

/** One node of the access graph. `parentId: null` makes it a root. */
export interface PagTreeNode {
  id: string;
  parentId: string | null;
  kind: PagNodeKind;
  label: string;
  /** Only policies carry a severity; it drives the risk legend filter. */
  severity?: PagSeverity | null;
  /** Extra key/value pairs shown in the node's hover tooltip. */
  meta?: Record<string, string | number>;
}

/* ── Display metadata ───────────────────────────────────────────────── */

/* Legend display order lives here rather than in the graph component so the
   legend and the node colors can never drift apart. */
export const PAG_KIND_ORDER: PagNodeKind[] = [
  "application",
  "account",
  "user",
  "group",
  "role",
  "policy",
];

export const PAG_KIND_META: Record<
  PagNodeKind,
  { label: string; color: string }
> = {
  application: { label: "Applications", color: "#FB923C" },
  account: { label: "Account Names", color: "#388BFD" },
  user: { label: "Users", color: "#22D3EE" },
  group: { label: "Groups", color: "#A78BFA" },
  role: { label: "Roles", color: "#F96C6C" },
  policy: { label: "Policies", color: "#34D399" },
};

export const PAG_SEVERITY_ORDER: PagSeverity[] = [
  "High",
  "Medium",
  "Low",
  "Uncategorized",
];

export const PAG_SEVERITY_META: Record<
  PagSeverity,
  { label: string; color: string }
> = {
  High: { label: "High Risk", color: "#F97316" },
  Medium: { label: "Medium Risk", color: "#FFB84D" },
  Low: { label: "Low Risk", color: "#00B8D4" },
  Uncategorized: { label: "Uncategorized", color: "#94A3B8" },
};

/** Unknown severities fall back to Uncategorized rather than crashing on a
 *  missing lookup, since severity is server-supplied free text. */
export const severityMeta = (s: PagSeverity | null | undefined) =>
  (s && PAG_SEVERITY_META[s]) || PAG_SEVERITY_META.Uncategorized;

/* ── Filter bar ─────────────────────────────────────────────────────── */

/* Department / Division / Application only. CSP is a level of the filter
   API's tree but NOT a filter of its own: one application is deployed on
   several clouds under the same appCode, so filtering by provider would
   split an application from itself. */
export interface PagFilters {
  /** Empty means every department, matching how `applications` reads. */
  departments: string[];
  divisions: string[];
  /** appCodes. Empty means every application, which is what each PAG
   *  request body already means by an empty `appCodes` list. */
  applications: string[];
}

/* ── Details table ──────────────────────────────────────────────────── */

export type PagCycle = "current" | "previous";

/** Policy grants for one account, split by the kind of identity holding
 *  them. `total` is the sum, and is what the donut prints in its centre. */
export interface PagPolicyBreakdown {
  groups: number;
  roles: number;
  serviceRoles: number;
  users: number;
  total: number;
}

/** Review-campaign metadata that the access payload does not carry.
 *  Supplied per account from the mock file until its real source exists. */
export interface PagReviewMeta {
  criticality: string;
  department: string;
  division: string;
  reviewCycle: string;
  dueDate: string;
  reviewers: string[];
}

/** One row of the Details table — an account's review for one cycle. */
export interface PagReviewRow {
  id: string;
  /** Carried through for the drill-down request body, which is keyed by
   *  appCode + accountId rather than the display name. */
  appCode: string;
  applicationName: string;
  criticality: string;
  department: string;
  division: string;
  accountId: string;
  accountName: string;
  reviewCycle: string;
  dueDate: string;
  reviewers: string[];
  policyCount: PagPolicyBreakdown;
  pending: number;
  completed: number;
}

/* Donut segments, in draw order. Kept beside the breakdown type so the
   chart, its legend, and the counts can never disagree. */
export const PAG_BREAKDOWN_SEGMENTS: {
  key: keyof Omit<PagPolicyBreakdown, "total">;
  label: string;
  color: string;
}[] = [
  { key: "groups", label: "Groups", color: PAG_KIND_META.group.color },
  { key: "roles", label: "Roles", color: PAG_KIND_META.role.color },
  /* Service roles have no node kind of their own in the graph, so they
     borrow the policy hue - the only one no other segment is using. */
  { key: "serviceRoles", label: "Service Roles", color: PAG_KIND_META.policy.color },
  { key: "users", label: "Users", color: PAG_KIND_META.user.color },
];

/* ══════════════════════════════════════════════════════════════════════
   TRANSFORMERS
   ══════════════════════════════════════════════════════════════════════ */

/**
 * Flattens one application into the graph's node list.
 *
 * Users, groups and roles are siblings under their account (that is how the
 * API nests them), and each one's policies hang directly off it — so a
 * branch is 4 levels deep, not 6. Policy `id` repeats across roles
 * (AdministratorAccess is id 4 everywhere), so node ids are prefixed with
 * the owner's id to stay unique.
 */
/* Takes EVERY selected application, not one. Each becomes its own root,
   which layoutTree already lays out side by side -- it walks a `roots`
   array and keeps a running leaf cursor, so several trees stack instead
   of overlapping. Ids are prefixed with `app:${appCode}` throughout, so
   the same account or policy under two applications stays two nodes. */
function addTreePolicies(
  ownerId: string,
  policies: PagPolicy[] | null | undefined,
  nodes: PagTreeNode[],
): void {
  for (const p of policies ?? []) {
    if (!p) continue;
    nodes.push({
      id: `${ownerId}|pol:${p.id}`,
      parentId: ownerId,
      kind: "policy",
      label: p.policyName || `Policy ${p.id}`,
      severity: p.severity,
      meta: { ARN: p.policyArn },
    });
  }
}

function addAccountTreeIdentities(
  acc: PagAccount,
  accId: string,
  nodes: PagTreeNode[],
): void {
  for (const u of acc.users ?? []) {
    if (!u) continue;
    const uId = `${accId}|usr:${u.userName}`;
    nodes.push({
      id: uId,
      parentId: accId,
      kind: "user",
      label: u.userName,
      meta: {
        "Access Keys": u.accessKeys?.length ?? 0,
        "MFA Devices": u.mfaDevices?.length ?? 0,
      },
    });
    addTreePolicies(uId, u.userPolicies, nodes);
  }

  for (const g of acc.groups ?? []) {
    if (!g) continue;
    const gId = `${accId}|grp:${g.groupName}`;
    nodes.push({
      id: gId,
      parentId: accId,
      kind: "group",
      label: g.groupName,
      meta: { Users: g.groupUsers?.length ?? 0 },
    });
    addTreePolicies(gId, g.groupPolicies ?? [], nodes);
  }

  for (const r of acc.roles ?? []) {
    if (!r) continue;
    const rId = `${accId}|rol:${r.roleName}`;
    nodes.push({
      id: rId,
      parentId: accId,
      kind: "role",
      label: r.roleName,
      meta: {
        "Service Type": r.serviceType,
        Instances: r.instances?.length ?? 0,
      },
    });
    addTreePolicies(rId, r.rolePolicies, nodes);
  }
}

function addAccountTreeNode(
  acc: PagAccount,
  appId: string,
  nodes: PagTreeNode[],
): void {
  const accId = `${appId}|acc:${acc.accountId}`;
  const users = acc.users ?? [];
  const groups = acc.groups ?? [];
  const roles = acc.roles ?? [];
  nodes.push({
    id: accId,
    parentId: appId,
    kind: "account",
    label: acc.accountName || acc.accountId,
    meta: {
      "Account ID": acc.accountId,
      Users: users.length,
      Groups: groups.length,
      Roles: roles.length,
    },
  });

  addAccountTreeIdentities(acc, accId, nodes);
}

export function buildPagTree(apps: PagApplication[]): PagTreeNode[] {
  const nodes: PagTreeNode[] = [];

  for (const app of apps ?? []) {
    if (!app) continue;
    const appId = `app:${app.appCode}`;
    const accountDetails = app.accountIdDetails ?? [];
    nodes.push({
      id: appId,
      parentId: null,
      kind: "application",
      label: app.appName || app.appCode,
      meta: {
        "App Code": app.appCode,
        Accounts: accountDetails.length,
      },
    });

    for (const acc of accountDetails) {
      if (!acc) continue;
      addAccountTreeNode(acc, appId, nodes);
    }
  }

  return nodes;
}

/* ══════════════════════════════════════════════════════════════════════
   NETWORK GRAPH MODEL

   The network view walks SEVEN levels — two more than buildPagTree, which
   hangs policies straight off the identity that holds them:

     L0  root "App"
     L1  Application
     L2  Account
     L3  rollup: "Users (n)" / "Groups (n)" / "Roles (n)"
     L4  User | Group | Role
     L5  that identity's policies, plus a group's member users
     L6  the member users' policies

   The L3 rollups are what keep the view readable. Without them, opening
   an account with 40 roles drops 40 leaves on the canvas at once; with
   them it drops one node that says how much is behind it.

   Both datasets come out of ONE pass. `initial*` is the collapsed opening
   view (root + applications), `full*` is the entire graph held in memory,
   so expanding a node is a lookup rather than a refetch.
   ══════════════════════════════════════════════════════════════════════ */

/** The three buckets an account rolls its identities up into. */
export type PagNetBucket = "users" | "groups" | "roles";

/** A rollup stands in for a whole bucket, so it is not one of the six
 *  entity kinds — but it borrows its bucket's colour and legend entry. */
export type PagNetNodeType = PagNodeKind | "rollup";

export interface PagNetNode {
  id: string;
  parentId: string | null;
  type: PagNetNodeType;
  /** Entity kind the legend files this node under. A rollup reports its
   *  bucket's kind, so hiding "Groups" also hides "Groups (n)". */
  kind: PagNodeKind;
  bucket?: PagNetBucket;
  /** Full and untruncated. The canvas label is shortened for legibility;
   *  this is what the tooltip prints. */
  label: string;
  level: number;
  /** Rollups only. */
  count?: number;
  /** Policies only; drives the risk colour and the risk legend filter. */
  severity?: PagSeverity;
  meta?: Record<string, string | number>;
}

export interface PagNetEdge {
  id: string;
  from: string;
  to: string;
}

export interface PagNetworkModel {
  initialNodes: PagNetNode[];
  initialEdges: PagNetEdge[];
  fullNodes: PagNetNode[];
  fullEdges: PagNetEdge[];
}

/** Synthetic hub every application hangs off. */
export const PAG_NET_ROOT_ID = "root:apps";

export const PAG_NET_BUCKET_KIND: Record<PagNetBucket, PagNodeKind> = {
  users: "user",
  groups: "group",
  roles: "role",
};

/* Node colours for the network view only. Deliberately NOT PAG_KIND_META:
   that palette is shared with the Policy Insights tree and the Details
   donut, neither of which is being restyled. */
/* Node hues, per theme.

   One palette used to serve both, which is what made half the graph wash
   out: these hues were picked against the dark canvas, and a pastel that
   glows on #0A1120 is nearly white on #FFFFFF. The light set is the same
   six hues taken darker so they hold their own against white.

   Account is the one that moves the other way. #102250 is excellent on
   white and all but invisible on the dark canvas -- a near-black disc on
   a near-black ground -- so dark mode lifts it to a royal blue and light
   mode keeps the navy it was always right for. */
export const PAG_NET_KIND_COLOR: Record<PagNodeKind, string> = {
  application: "#F1A848",
  account: "#5478D4",
  group: "#8A84E2",
  user: "#79BEEE",
  role: "#ff7e80",
  policy: "#78D5D7",
};

export const PAG_NET_KIND_COLOR_LIGHT: Record<PagNodeKind, string> = {
  application: "#C97C10",
  account: "#102250",
  group: "#5B52C9",
  user: "#2478B8",
  role: "#DB4A4D",
  policy: "#1F8E90",
};

/** The node hue for a kind in the theme currently on screen. Every canvas
 *  and every legend goes through here, so the two graphs and the key they
 *  share cannot disagree about what colour an Account is. */
export const pagNetKindColor = (kind: PagNodeKind, isDark: boolean) =>
  (isDark ? PAG_NET_KIND_COLOR : PAG_NET_KIND_COLOR_LIGHT)[kind];

/** Risk reads as the policy label's colour, so the risk tier of the
 *  legend filters on exactly what the canvas is drawing. */
export const PAG_NET_RISK_COLOR: Record<PagSeverity, string> = {
  High: "#FF0000",
  Medium: "#FFA500",
  Low: "#FFE500",
  Uncategorized: "#3FA7D6",
};

/* Risk is the policy LABEL's colour, so it is read as text and needs the
   contrast text needs. On white the dark set is unreadable -- #FFE500 in
   particular is simply not there -- so each tier darkens to the point it
   can be read, keeping the red/amber/yellow/blue ladder intact. */
export const PAG_NET_RISK_COLOR_LIGHT: Record<PagSeverity, string> = {
  High: "#C81E1E",
  Medium: "#B45309",
  Low: "#8A6D00",
  Uncategorized: "#2478B8",
};

export const pagNetRiskColor = (
  s: PagSeverity | null | undefined,
  isDark: boolean,
) => {
  const palette = isDark ? PAG_NET_RISK_COLOR : PAG_NET_RISK_COLOR_LIGHT;
  return (s && palette[s]) || palette.Uncategorized;
};

/**
 * Every application as one 7-level graph, collapsed view and full graph
 * built together in a single walk of the payload.
 *
 * Node ids are path-prefixed because IAM names are only unique within
 * their parent — the same role name recurs across accounts, and policy id
 * 4 (AdministratorAccess) is attached to nearly everything. Rollup ids
 * carry their bucket (`|bucket:roles`), which is what lets a click on a
 * rollup be resolved without a second lookup table.
 */
type AddNetNodeFn = (node: PagNetNode, initial?: boolean) => void;
type AddNetPoliciesFn = (ownerId: string, level: number, policies?: PagPolicy[]) => void;

interface PagNetContext {
  add: AddNetNodeFn;
  addPolicies: AddNetPoliciesFn;
}

function addGroupMemberNetworkNodes(
  group: PagGroup,
  groupId: string,
  ctx: PagNetContext,
): void {
  for (const m of group.groupUsers ?? []) {
    if (!m) continue;
    const mId = `${groupId}|mem:${m.userName}`;
    ctx.add({
      id: mId,
      parentId: groupId,
      type: "user",
      kind: "user",
      label: m.userName,
      level: 5,
      meta: {
        Group: group.groupName,
        "Access Keys": m.accessKeys?.length ?? 0,
        "MFA Devices": m.mfaDevices?.length ?? 0,
      },
    });
    ctx.addPolicies(mId, 6, m.userPolicies);
  }
}

function addGroupNetworkNodes(
  groups: PagGroup[],
  groupsId: string,
  accId: string,
  ctx: PagNetContext,
): void {
  for (const g of groups) {
    if (!g) continue;
    const gId = `${accId}|grp:${g.groupName}`;
    ctx.add({
      id: gId,
      parentId: groupsId,
      type: "group",
      kind: "group",
      label: g.groupName,
      level: 4,
      meta: {
        Members: g.groupUsers?.length ?? 0,
        Policies: g.groupPolicies?.length ?? 0,
      },
    });
    ctx.addPolicies(gId, 5, g.groupPolicies);
    addGroupMemberNetworkNodes(g, gId, ctx);
  }
}

function addUserNetworkNodes(
  users: PagUser[],
  usersId: string,
  accId: string,
  ctx: PagNetContext,
): void {
  for (const u of users) {
    if (!u) continue;
    const uId = `${accId}|usr:${u.userName}`;
    ctx.add({
      id: uId,
      parentId: usersId,
      type: "user",
      kind: "user",
      label: u.userName,
      level: 4,
      meta: {
        "Access Keys": u.accessKeys?.length ?? 0,
        "MFA Devices": u.mfaDevices?.length ?? 0,
        Policies: u.userPolicies?.length ?? 0,
      },
    });
    ctx.addPolicies(uId, 5, u.userPolicies);
  }
}

function addRoleNetworkNodes(
  roles: PagRole[],
  rolesId: string,
  accId: string,
  ctx: PagNetContext,
): void {
  for (const r of roles) {
    if (!r) continue;
    const rId = `${accId}|rol:${r.roleName}`;
    ctx.add({
      id: rId,
      parentId: rolesId,
      type: "role",
      kind: "role",
      label: r.roleName,
      level: 4,
      meta: {
        "Service Type": r.serviceType,
        Instances: r.instances?.length ?? 0,
        Policies: r.rolePolicies?.length ?? 0,
      },
    });
    ctx.addPolicies(rId, 5, r.rolePolicies);
  }
}

function addAccountRollupNode(
  bucket: PagNetBucket,
  label: string,
  count: number,
  accId: string,
  accountName: string,
  add: AddNetNodeFn,
): string | null {
  if (!count) return null;
  const id = `${accId}|bucket:${bucket}`;
  add({
    id,
    parentId: accId,
    type: "rollup",
    kind: PAG_NET_BUCKET_KIND[bucket],
    bucket,
    label,
    level: 3,
    count,
    meta: { Account: accountName },
  });
  return id;
}

function addAccountNetworkNodes(
  acc: PagAccount,
  appId: string,
  ctx: PagNetContext,
): void {
  const accId = `${appId}|acc:${acc.accountId}`;
  const users = acc.users ?? [];
  const groups = acc.groups ?? [];
  const roles = acc.roles ?? [];

  ctx.add({
    id: accId,
    parentId: appId,
    type: "account",
    kind: "account",
    label: acc.accountName || acc.accountId,
    level: 2,
    meta: {
      "Account ID": acc.accountId,
      Users: users.length,
      Groups: groups.length,
      Roles: roles.length,
    },
  });

  const groupsId = addAccountRollupNode(
    "groups",
    "Groups",
    groups.length,
    accId,
    acc.accountName,
    ctx.add,
  );
  const usersId = addAccountRollupNode(
    "users",
    "Users",
    users.length,
    accId,
    acc.accountName,
    ctx.add,
  );
  const rolesId = addAccountRollupNode(
    "roles",
    "Roles",
    roles.length,
    accId,
    acc.accountName,
    ctx.add,
  );

  if (usersId) {
    addUserNetworkNodes(users, usersId, accId, ctx);
  }
  if (groupsId) {
    addGroupNetworkNodes(groups, groupsId, accId, ctx);
  }
  if (rolesId) {
    addRoleNetworkNodes(roles, rolesId, accId, ctx);
  }
}

/**
 * Every application as one 7-level graph, collapsed view and full graph
 * built together in a single walk of the payload.
 *
 * Node ids are path-prefixed because IAM names are only unique within
 * their parent — the same role name recurs across accounts, and policy id
 * 4 (AdministratorAccess) is attached to nearly everything. Rollup ids
 * carry their bucket (`|bucket:roles`), which is what lets a click on a
 * rollup be resolved without a second lookup table.
 */
export function buildPagNetworkModel(apps: PagApplication[]): PagNetworkModel {
  const fullNodes: PagNetNode[] = [];
  const fullEdges: PagNetEdge[] = [];
  const initialNodes: PagNetNode[] = [];
  const initialEdges: PagNetEdge[] = [];

  /* vis-data throws on a duplicate id rather than ignoring it, so both
     lists are guarded even though the prefixing should make them unique. */
  const seenNodes = new Set<string>();
  const seenEdges = new Set<string>();

  const add: AddNetNodeFn = (node, initial = false) => {
    if (seenNodes.has(node.id)) return;
    seenNodes.add(node.id);
    fullNodes.push(node);
    if (initial) initialNodes.push(node);

    if (!node.parentId) return;
    const id = `${node.parentId}-${node.id}`;
    if (seenEdges.has(id)) return;
    seenEdges.add(id);
    const edge: PagNetEdge = { id, from: node.parentId, to: node.id };
    fullEdges.push(edge);
    if (initial) initialEdges.push(edge);
  };

  const addPolicies: AddNetPoliciesFn = (ownerId, level, policies) => {
    for (const p of policies ?? []) {
      add({
        id: `${ownerId}|pol:${p.id}`,
        parentId: ownerId,
        type: "policy",
        kind: "policy",
        label: p.policyName,
        level,
        severity: p.severity,
        meta: { ARN: p.policyArn },
      });
    }
  };

  const ctx: PagNetContext = { add, addPolicies };

  add(
    {
      id: PAG_NET_ROOT_ID,
      parentId: null,
      type: "application",
      kind: "application",
      label: "App",
      level: 0,
      meta: { Applications: apps.length },
    },
    true,
  );

  for (const app of apps ?? []) {
    if (!app) continue;
    const appId = `app:${app.appCode}`;
    const accountDetails = app.accountIdDetails ?? [];
    add(
      {
        id: appId,
        parentId: PAG_NET_ROOT_ID,
        type: "application",
        kind: "application",
        label: app.appName || app.appCode,
        level: 1,
        meta: { "App Code": app.appCode, Accounts: accountDetails.length },
      },
      true,
    );

    for (const acc of accountDetails) {
      if (!acc) continue;
      addAccountNetworkNodes(acc, appId, ctx);
    }
  }

  return { initialNodes, initialEdges, fullNodes, fullEdges };
}

/**
 * Policy grants for one account, split by identity kind.
 *
 * A role counts as a SERVICE role when its serviceType is anything other
 * than "Others" — that field already marks AWS service-linked roles
 * (guardduty, config, lambda, ...), so no name matching is needed.
 */
export function buildPolicyBreakdown(acc: PagAccount): PagPolicyBreakdown {
  const sum = (arr: PagPolicy[][]) =>
    arr.reduce((n, policies) => n + policies.length, 0);

  const groups = sum(acc.groups.map((g) => g.groupPolicies ?? []));
  const users = sum(acc.users.map((u) => u.userPolicies));
  const roles = sum(
    acc.roles.filter((r) => r.serviceType === "Others").map((r) => r.rolePolicies),
  );
  const serviceRoles = sum(
    acc.roles.filter((r) => r.serviceType !== "Others").map((r) => r.rolePolicies),
  );

  return {
    groups,
    roles,
    serviceRoles,
    users,
    total: groups + roles + serviceRoles + users,
  };
}

/* Stable pseudo-random share in 0.60..0.94 derived from the account id, so
   the mocked pending/completed split is different per account but never
   jitters between renders. */
function pendingShare(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i += 1) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return 0.6 + (h % 35) / 100;
}

/** Months in one review period, from its label. */
function cycleMonths(reviewCycle: string): number {
  const c = reviewCycle.toLowerCase();
  if (c.includes("quarter")) return 3;
  if (c.includes("month")) return 1;
  if (c.includes("annual") || c.includes("year")) return 12;
  return 6; // Half-yearly
}

function shiftMonths(iso: string, months: number): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  d.setMonth(d.getMonth() - months);
  return d.toISOString().slice(0, 10);
}

/**
 * Builds the Details table — one row per account of the selected
 * application, for the selected review cycle.
 *
 * The policy counts are real (derived from the access data). The campaign
 * fields come from `metaByAccount`, and pending/completed are mocked: the
 * payload carries no review-progress data. The previous cycle is modelled
 * as fully signed off, one period earlier.
 */
export function buildPagReviewRows(
  app: PagApplication | null,
  cycle: PagCycle,
  metaByAccount: Record<string, PagReviewMeta>,
): PagReviewRow[] {
  if (!app) return [];

  return app.accountIdDetails.map((acc) => {
    const meta = metaByAccount[acc.accountId];
    const reviewCycle = meta?.reviewCycle ?? "Half-yearly";
    const policyCount = buildPolicyBreakdown(acc);

    const completed =
      cycle === "previous"
        ? policyCount.total
        : policyCount.total - Math.round(policyCount.total * pendingShare(acc.accountId));

    const dueDate =
      cycle === "previous"
        ? shiftMonths(meta?.dueDate ?? "", cycleMonths(reviewCycle))
        : (meta?.dueDate ?? "");

    return {
      id: `${app.appCode}|${acc.accountId}|${cycle}`,
      appCode: app.appCode,
      applicationName: app.appName,
      criticality: meta?.criticality ?? "-",
      department: meta?.department ?? "-",
      division: meta?.division ?? "-",
      accountId: acc.accountId,
      accountName: acc.accountName,
      reviewCycle,
      dueDate,
      reviewers: meta?.reviewers ?? [],
      policyCount,
      pending: policyCount.total - completed,
      completed,
    };
  });
}
